// Repos-grid enable/disable (#972) — the state half of the row toggle, split
// from the route the way `createWebhookTest` is. In-flight PATCHes are keyed
// BY full_name, never one "which row is toggling" slot: two rows are two
// independent writes, and a shared slot re-enables row A's button the moment
// row B starts. After a success the LIST is reloaded — `/v1/repos` serves
// enabled repos only, so a disabled row honestly vanishes instead of being
// patched into a shape the server no longer answers.

import { t } from '../i18n';
import { createKeyedMutation } from './keyed-mutation';
import { type RepoRow, setRepoEnabled } from './repos';
import { canOwnerWrite } from './roles';
import type { ToastTone } from './toast';

export type RepoToggleController = {
  /** True while THIS row's PATCH is in flight. */
  readonly isToggling: (fullName: string) => boolean;
  /**
   * Whether to offer the control at all — the `canOwnerWrite` mirror
   * (`write:repos`), SHUT while `/v1/me` is in flight: mid-load `role` is
   * `undefined`, the same shape as the PAT / machine credential that KEEPS it.
   */
  readonly offered: () => boolean;
  /** Flip a row. A second call for the same repo mid-flight is dropped. */
  readonly run: (repo: RepoRow) => void;
};

export function createRepoToggle(input: {
  readonly token: string | null;
  /** The route's `/v1/me` query — read reactively, never destructured. */
  readonly me: {
    readonly isLoading: boolean;
    readonly data: { readonly role?: string | null } | undefined;
  };
  readonly reload: () => void;
  readonly notify: (message: string, tone: ToastTone) => void;
}): RepoToggleController {
  // `createKeyedMutation` owns the per-row lock. It used to be split across two
  // functions here — `run` READ the flag, `flip` WROTE it — so the guard could
  // be forgotten at a third entry point without either half looking wrong.
  const toggle = createKeyedMutation({
    fallback: t('repos.toggle_failed'),
    onError: (message) => input.notify(message, 'error'),
  });

  return {
    isToggling: toggle.isBusy,
    offered: () => !input.me.isLoading && canOwnerWrite(input.me.data?.role),
    run: (repo) => {
      void toggle.run(repo.full_name, async () => {
        await setRepoEnabled(repo, !repo.enabled, input.token);
        input.reload();
      });
    },
  };
}
